import React, { useReducer, useEffect, useState } from 'react';
import {
    Grid,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Alert,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@mui/material';
import SelectMulti from '../../../../components/select/SelectMulti';
import MonthPickerComponent from '../../../../components/date/MonthPickerComponent';
import {toast} from 'react-toastify';
import axios from "axios";
import { FINANCIAL_PLATFORMS } from '../../../../constants/financialPlatforms';
import useApi from '../../../../hooks/useApi';
import {fetchDspCompanies} from '../../../../API/FinancialTools';


const initialState = {
    month: '',
    platform: '',
    publisher: '',
    advertiser: '',
    reason: '',
    deduction: '',
    approved_deduction: '',
    file_link: '',
    file: null,
    comment: '',
    id: '',
    type: '',
};

function getInitialState(editRow) {
    return {
        ...initialState,
        platform: editRow.platform || '',
        publisher: editRow.publisherOriginal || '',
        advertiser: editRow.advertiserOriginal || '',
        reason: editRow.reason || '',
        deduction: editRow.deduction || '',
        approved_deduction: editRow.approved_deduction || '',
        file_link: editRow.file || '',
        comment: editRow.revision_comment || '',
        id: editRow.id || '',
        type: editRow.type || '',
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'set':
            return { ...state, [action.field]: action.value };
        case 'reset':
            return initialState;
        default:
            return state;
    }
}


function EditRowDialog({
                           open,
                           handleClose,
                           ssp,
                           editRow,
                           onSubmitSuccess,
                       }) {
    const [state, dispatch] = useReducer(reducer, getInitialState(editRow));
    const [loading, setLoading] = useState(true);
    const { data: dsp = [] } = useApi(fetchDspCompanies);

    useEffect(() => {
            setLoading(false);
    }, [editRow]);

    useEffect(() => {
        dispatch({ type: 'set', field: 'platform', value: editRow.platform });
        dispatch({ type: 'set', field: 'publisher', value: editRow.publisherOriginal });
        dispatch({ type: 'set', field: 'advertiser', value: editRow.advertiserOriginal });
        dispatch({ type: 'set', field: 'reason', value: editRow.reason });
        dispatch({ type: 'set', field: 'deduction', value: editRow.deduction });
        dispatch({ type: 'set', field: 'approved_deduction', value: editRow.approved_deduction });
        dispatch({ type: 'set', field: 'file_link', value: editRow.file });
        dispatch({ type: 'set', field: 'comment', value: editRow.revision_comment });
        dispatch({ type: 'set', field: 'id', value: editRow.id });
        dispatch({ type: 'set', field: 'month', value: editRow.period });
        dispatch({ type: 'set', field: 'type', value: editRow.type || '' });
    }, [editRow]);


    const [endpoint, setEndpoint] = useState('/api/financialtools/deduction-control/edit');
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const handleMonthChange = (selectedMonth) => {
        const periodString = `${selectedMonth.year}-${selectedMonth.month}`;
        dispatch({ type: 'set', field: 'month', value: periodString });
    };
    const handleInputChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.value });
    };

    const handleInputFileChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.files[0] });
    };

    const validateFormData = () => {
        const requiredFields = [state.platform, state.publisher, state.advertiser, state.reason, state.deduction, state.type];
        return requiredFields.every(field => Boolean(field));
    };

    const sendToServer = async () => {
        if (!validateFormData()) {
            toast.error('Fill required fields first!');
            return;
        }

        try {
            const userId = localStorage.getItem('user_id');
            const formData = new FormData();
            const formFields = {
                file: state.file,
                user_id: userId,
                platform: state.platform,
                publisher: state.publisher,
                advertiser: state.advertiser,
                reason: state.reason,
                deduction: state.deduction,
                file_link: state.file_link,
                month: state.month,
                comment: state.comment,
                id: state.id,
                type: state.type
            };

            Object.keys(formFields).forEach(key => {
                if (formFields[key]) formData.append(key, formFields[key]);
            });

            const response = await axios.post(`${BASE_URL}${endpoint}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.success) {
                toast.success(response.data.message);
                onSubmitSuccess();
                dispatch({ type: 'reset' });
                handleClose();
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            toast.error(`An error occurred while sending data: ${error.message}`);
            console.error('An error occurred while sending data:', error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Edit {editRow.publisherDisplay}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Grid container justifyContent="center">
                        <Grid item>
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <CircularProgress size={150}/>
                            </Box>
                        </Grid>
                    </Grid>
                ):(<>
                <Alert variant="outlined" severity="info" color="info" sx={{marginBottom: "20px"}}>
                    Please provide the new values in this form for updating.<br/>
                </Alert>

                <div
                    style={{
                        userSelect: 'none',
                        borderRadius: '5px',
                        textAlign: "center",
                        backgroundColor: '#f0f3f5',
                        padding: '2px',
                        cursor: 'pointer',
                        border: '1px solid #dddfe1',
                    }}
                >
                    <div>
                        <InputLabel id="publisher-label">Choose a period</InputLabel>
                    </div>
                    <div>
                        <MonthPickerComponent onMonthChange={handleMonthChange} initialMonthYear={editRow.period}/>
                    </div>
                </div>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="platform-label">Platform</InputLabel>
                    <Select
                        labelId="platform-label"
                        id="platform"
                        name="platform"
                        value={(state.platform ?? editRow.platform ?? "").toLowerCase()}
                        onChange={handleInputChange}
                        required={true}
                        label="Platform"
                    >
                        {FINANCIAL_PLATFORMS.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="publisher-label">Publisher</InputLabel>
                    <Select
                        labelId="publisher-label"
                        id="publisher"
                        name="publisher"
                        value={state.publisher ?? editRow.publisherOriginal}
                        onChange={handleInputChange}
                        required={true}
                        label="Publisher"
                    >
                        {ssp.map((ssp) => (
                            <MenuItem key={ssp.value.id} value={ssp.value.id}>
                                {ssp.value.name} (ID: {ssp.value.id})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="advertiser-label">Advertiser</InputLabel>
                    <Select
                        labelId="advertiser-label"
                        id="advertiser"
                        name="advertiser"
                        value={state.advertiser}
                        onChange={handleInputChange}
                        required={true}
                        label="Advertiser"
                    >
                        {dsp.map((item) => (
                            <MenuItem key={item.value.id} value={item.value.id}>
                                {item.value.name} (ID: {item.value.id})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="reason-label">Reason</InputLabel>
                    <Select
                        labelId="reason-label"
                        id="reason"
                        name="reason"
                        value={state.reason}
                        onChange={handleInputChange}
                        required={true}
                        label="Reason"
                    >
                        <MenuItem value={'Discrepancy'}>Discrepancy</MenuItem>
                        <MenuItem value={'IVT Adjustment'}>IVT Adjustment</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    margin="dense"
                    id="deduction"
                    label="Deduction"
                    type="number"
                    fullWidth
                    name="deduction"
                    value={state.deduction}
                    onChange={handleInputChange}
                    required={true}
                />

                <FormControl fullWidth margin="dense">
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        id="type"
                        name="type"
                        value={state.type}
                        onChange={handleInputChange}
                        required={true}
                        label="Type"
                    >
                        <MenuItem value={'Cost'}>Cost</MenuItem>
                        <MenuItem value={'Revenue'}>Revenue</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    margin="dense"
                    id="approved_deduction"
                    label="Approved Deduction"
                    type="number"
                    fullWidth
                    name="approved_deduction"
                    value={state.approved_deduction}
                    onChange={handleInputChange}
                    disabled={true}
                    InputProps={{
                        readOnly: true,
                    }}
                />


                <TextField
                    margin="dense"
                    id="file_link"
                    label="File Link"
                    type="text"
                    fullWidth
                    name="file_link"
                    value={state.file_link}
                    onChange={handleInputChange}
                />

                <div>
                    <InputLabel id="publisher-label">Or choose a file</InputLabel>
                </div>
                <div>
                    <input
                        type="file"
                        id="file"
                        name="file"
                        onChange={handleInputFileChange}
                    />
                </div>

                <TextField
                    margin="dense"
                    id="comment"
                    label="Revision Comment"
                    type="text"
                    fullWidth
                    name="comment"
                    value={state.comment}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                /></>)}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                {!loading && (
                    <Button onClick={sendToServer} color="primary">
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default EditRowDialog;